import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { ProcessDiagram } from "@/components/Diagrams";
import { APPROACH_STAGES, APPROACH_PRINCIPLES, ENGAGEMENT_STEPS } from "@/data/site";

export default function Approach() {
  return (
    <>
      <SEO
        title="Our Approach"
        description="Our five-stage consulting methodology — Discover, Assess, Design, Implement, Improve — and the principles of risk-based, business-aligned, collaborative engagement behind it."
        path="/approach"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Our Approach", path: "/approach" }]}
      />
      <PageHero
        eyebrow="Our Approach"
        title="A Disciplined Method, Applied with Judgement"
        description="Every engagement follows a proven five-stage structure — adapted to your objectives, environment, risk profile and current maturity."
        breadcrumb="Our Approach"
      />

      {/* Five-stage methodology */}
      <section className="bg-white py-16 sm:py-24" data-testid="approach-methodology">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <p className="eyebrow mb-4">The Method</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Discover → Assess → Design → Implement → Improve
            </h2>
          </Reveal>
          <div className="mt-14">
            <ProcessDiagram stages={APPROACH_STAGES} />
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-mist py-16 sm:py-24" data-testid="approach-principles">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <p className="eyebrow mb-4">How We Work</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Principles That Shape Every Engagement
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {APPROACH_PRINCIPLES.map((p, i) => (
              <Reveal key={p.name} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl bg-white border border-slate-200 p-7">
                  <span className="font-mono text-xs font-semibold text-teal-dark" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-ink">{p.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{p.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What engagement can look like */}
      <section className="relative bg-gradient-to-br from-navy via-navy-800 to-navy-900 py-16 sm:py-24 overflow-hidden" data-testid="engagement-steps">
        <div className="absolute inset-0 grid-pattern-dark" aria-hidden="true" />
        <div className="container-site relative">
          <Reveal className="max-w-2xl">
            <p className="eyebrow-light mb-4">In Practice</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              What Engagement Can Look Like
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300">
              A typical engagement moves through these stages. We do not publish guaranteed timelines:
              duration depends on scope, organizational maturity and the outcomes you need.
            </p>
          </Reveal>
          <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ENGAGEMENT_STEPS.map((step, i) => (
              <Reveal key={step.name} delay={(i % 4) * 0.08}>
                <li className="h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6">
                  <span className="font-mono text-xs font-semibold text-teal" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heading text-base font-semibold text-white">{step.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{step.detail}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <CTASection
        title="Ready to Put a Disciplined Method to Work?"
        description="Start with an initial consultation — we will listen first, then outline how an engagement could be structured for your environment."
      />
    </>
  );
}
